import React from "react";
import {
  Menu,
  MenuItem,
  Table as MuiTable,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import SouthIcon from "@mui/icons-material/South";
import NorthIcon from "@mui/icons-material/North";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Cell from "src/Tables/TableTemplate/Cell";
import { ITableUI } from "src/Tables/TableTemplate/tableTemplate";
import { Header, IAccordionTableRow } from "src/Interfaces/TableInterfaces";
import PortfoSegregationModal from "../StatusSummary/PortfoSegregationModal";

const headerCellClassName =
  "!cursor-default !font-black text-black !text-center";

const sortableHeaderCellClassName =
  "!cursor-pointer !bg-sky-50 hover:!bg-sky-100 !transition-all !font-black text-black !text-center";

const DARK_TEXT_HEADER_NAMES = new Set([
  "MinPercent",
  "MaxPercent",
  "SurplusDeficit",
]);

const getColumnTextClassName = (headerName: string) =>
  DARK_TEXT_HEADER_NAMES.has(headerName) ? "!text-[#4b4762]" : "";

const groupDynamicHeaders = (headers: Header[]) => {
  const groups: { groupName: string; headers: Header[] }[] = [];

  headers.forEach((header) => {
    const lastGroup = groups[groups.length - 1];
    if (lastGroup && lastGroup.groupName === header.groupName) {
      lastGroup.headers.push(header);
      return;
    }

    groups.push({ groupName: header.groupName!, headers: [header] });
  });

  return groups;
};

const filterDynamicHeaders = (headers: Header[], showHiddenCols?: boolean) => {
  if (showHiddenCols == null) return headers;

  return headers.filter((header) =>
    showHiddenCols
      ? header.positionTypeStr === "FirstPage" ||
        header.positionTypeStr === "DefaultHidden"
      : header.positionTypeStr === "FirstPage",
  );
};

const renderHeaderText = (text?: string | null) => {
  const lines = text?.split("\n") ?? [];
  if (lines.length <= 1) return <span>{text}</span>;

  return (
    <span className="leading-5">
      <span className="block">{lines[0]}</span>
      {lines.slice(1).map((line, index) => (
        <span key={`${line}-${index}`} className="block !font-normal text-[10px] leading-4">
          {line}
        </span>
      ))}
    </span>
  );
};

const getGroupCellClassName = (
  header: Header,
  groups: { groupName: string; headers: Header[] }[],
) => {
  for (let groupIndex = 0; groupIndex < groups.length; groupIndex++) {
    const group = groups[groupIndex];
    const indexInGroup = group.headers.findIndex(
      (h) => h.headerName === header.headerName,
    );
    if (indexInGroup === -1) continue;

    const isFirstInGroup = indexInGroup === 0;
    const isLastInGroup = indexInGroup === group.headers.length - 1;
    const classes: string[] = [];

    if (isFirstInGroup) {
      classes.push("!border-r-1", "!border-r-sky-200");
    }
    if (groupIndex === 0 && isLastInGroup) {
      classes.push("!border-l-1", "!border-l-sky-200");
    }

    return classes.join(" ");
  }

  return "";
};

const mergeTableRows = <TRow extends IAccordionTableRow>(rows: TRow[]) => {
  const seen = new Set<string>();

  return rows.filter((row) => {
    const key = `${row.id}-${row.row}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const buildVisibleRows = <TRow extends IAccordionTableRow>(
  allRows: TRow[],
  collapsedGroups: Set<string>,
) => {
  const result: TRow[] = [];
  const expandedDetailsShown = new Set<string>();

  const isGroupOpen = (groupName: string) => !collapsedGroups.has(groupName);

  allRows.forEach((row) => {
    if (!row.groupName) {
      result.push(row);
      return;
    }

    if (row.isFooter) {
      result.push(row);
      if (
        isGroupOpen(row.groupName) &&
        !expandedDetailsShown.has(row.groupName)
      ) {
        expandedDetailsShown.add(row.groupName);
        result.push(
          ...allRows.filter(
            (item) => item.groupName === row.groupName && !item.isFooter,
          ),
        );
      }
      return;
    }

    const hasFooter = allRows.some(
      (item) => item.groupName === row.groupName && item.isFooter,
    );
    if (hasFooter) return;

    if (isGroupOpen(row.groupName)) result.push(row);
  });

  return result;
};

const getStableRowKey = (row: IAccordionTableRow) =>
  `${row.isFooter ? "footer" : "row"}-${row.groupName ?? "ungrouped"}-${row.id}`;

export interface IGroupTablePersistentStateProps {
  persistentCollapsedGroupsRef?: React.MutableRefObject<Set<string>>;
  persistentScrollPositionRef?: React.MutableRefObject<{ x: number; y: number }>;
  groupTitleHeaderName?: string;
}

const GroupTableUI = <TRow extends IAccordionTableRow>({
  reports,
  onAction,
  tableBoxClassName,
  sortingType,
  setSortingType,
  onTableHeaderClick,
  showHiddenCols,
  persistentCollapsedGroupsRef,
  persistentScrollPositionRef,
  groupTitleHeaderName,
  groupHeaderClassName,
  groupHeaderStyle,
  columnWidthPercentages,
}: ITableUI<TRow> &
  IGroupTablePersistentStateProps & {
    groupHeaderClassName?: (groupName: string) => string;
    groupHeaderStyle?: (groupName: string) => React.CSSProperties | undefined;
    columnWidthPercentages?: Record<string, number>;
  }) => {
  const firstHeaderRowRef = React.useRef<HTMLTableRowElement>(null);
  const tableScrollRef = React.useRef<HTMLDivElement | null>(null);
  const internalScrollPositionRef = React.useRef({ x: 0, y: 0 });
  const scrollPositionRef =
    persistentScrollPositionRef ?? internalScrollPositionRef;
  const fixedFirstHeaderCellRefs = React.useRef<
    Record<string, HTMLTableCellElement | null>
  >({});
  const [firstRowHeight, setFirstRowHeight] = React.useState(0);
  const [fixedFirstRightOffsets, setFixedFirstRightOffsets] = React.useState<
    Record<string, number>
  >({});
  const [segregationModal, setSegregationModal] = React.useState<{
    accountId: number;
    tag: string;
  } | null>(null);
  const [contextMenuAnchor, setContextMenuAnchor] =
    React.useState<HTMLElement | null>(null);
  const [contextMenuData, setContextMenuData] = React.useState<{
    accountId: number;
    tag: string;
  } | null>(null);
  const [collapsedGroups, setCollapsedGroups] = React.useState<Set<string>>(
    () => new Set(persistentCollapsedGroupsRef?.current ?? []),
  );

  React.useEffect(() => {
    if (!reports) return;

    const availableGroups = new Set(
      [...(reports.report ?? []), ...(reports.footer ?? [])]
        .map((row) => row.groupName)
        .filter((groupName): groupName is string => Boolean(groupName)),
    );

    setCollapsedGroups((prev) => {
      const next = new Set(
        Array.from(prev).filter((groupName) => availableGroups.has(groupName)),
      );
      if (persistentCollapsedGroupsRef) persistentCollapsedGroupsRef.current = next;
      return next.size === prev.size ? prev : next;
    });
  }, [reports, persistentCollapsedGroupsRef]);

  React.useLayoutEffect(() => {
    const tableScroll = tableScrollRef.current;
    if (!tableScroll) return;

    tableScroll.scrollLeft = scrollPositionRef.current.x;
    tableScroll.scrollTop = scrollPositionRef.current.y;
  }, [reports]);

  React.useLayoutEffect(() => {
    const row = firstHeaderRowRef.current;
    if (!row) return;

    const updateHeight = () => {
      setFirstRowHeight(row.getBoundingClientRect().height);
    };

    updateHeight();
    const observer = new ResizeObserver(updateHeight);
    observer.observe(row);
    return () => observer.disconnect();
  }, [reports]);

  React.useLayoutEffect(() => {
    if (!reports) return;

    const updateOffsets = () => {
      const staticHeaders = (reports.translatedHeaders ?? []).filter(
        (header) => !header.groupName,
      );
      const fixedFirst = staticHeaders.filter(
        (header) => header.positionTypeStr === "FixedFirst",
      );
      const offsets: Record<string, number> = {};
      let accumulatedRight = 0;

      fixedFirst.forEach((header) => {
        offsets[header.headerName] = accumulatedRight;
        const cell = fixedFirstHeaderCellRefs.current[header.headerName];
        accumulatedRight += cell?.getBoundingClientRect().width ?? 0;
      });

      setFixedFirstRightOffsets(offsets);
    };

    updateOffsets();
    const row = firstHeaderRowRef.current;
    if (!row) return;

    const observer = new ResizeObserver(updateOffsets);
    observer.observe(row);
    return () => observer.disconnect();
  }, [reports]);

  if (!reports) return null;

  const headers = reports.translatedHeaders ?? [];
  const staticHeaders = headers.filter((header) => !header.groupName);
  const fixedFirstHeaders = staticHeaders.filter(
    (header) => header.positionTypeStr === "FixedFirst",
  );
  const scrollableStaticHeaders = staticHeaders.filter(
    (header) => header.positionTypeStr !== "FixedFirst",
  );
  const dynamicHeaders = filterDynamicHeaders(
    headers.filter((header) => header.groupName),
    showHiddenCols,
  );
  const dynamicGroups = groupDynamicHeaders(dynamicHeaders);

  const allRows = mergeTableRows([
    ...(reports.report ?? []),
    ...(reports.footer ?? []),
  ]);
  const visibleRows = buildVisibleRows(allRows, collapsedGroups);

  const toggleGroup = (groupName: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupName)) next.delete(groupName);
      else next.add(groupName);
      if (persistentCollapsedGroupsRef) persistentCollapsedGroupsRef.current = next;
      return next;
    });
  };

  const expandIconHeaderName =
    fixedFirstHeaders[0]?.headerName ?? scrollableStaticHeaders[0]?.headerName;

  const getFixedFirstInnerCellClassName = (header: Header) =>
    header.translateHeaderName === "#" ? "" : "inner-fixed-column";

  const getFixedFirstRightOffset = (header: Header) =>
    fixedFirstRightOffsets[header.headerName] ?? 0;

  const getGroupHeaderSx = (groupName: string) => {
    const backgroundColor = groupHeaderStyle?.(groupName)?.backgroundColor;

    return backgroundColor
      ? {
          "&&&&&": {
            backgroundColor: `${backgroundColor} !important`,
          },
        }
      : undefined;
  };

  const tableHeaderClickHandler = (header: Header) => {
    if ((sortingType || sortingType === "") && setSortingType)
      setSortingType(header.headerName);
    if (onTableHeaderClick) onTableHeaderClick(header);
  };

  const handleCloseContextMenu = () => {
    setContextMenuAnchor(null);
    setContextMenuData(null);
  };

  const dynamicCellRightClickHandler = (
    e: React.MouseEvent<HTMLElement>,
    row: TRow,
    header: Header,
  ) => {
    e.preventDefault();
    e.stopPropagation();
    const tag = header.tag != null && header.tag !== "" ? header.tag : null;
    if (tag == null || Number.isNaN(tag)) return;
    setContextMenuAnchor(e.currentTarget);
    setContextMenuData({ accountId: row.id, tag: tag as string });
  };

  const handleTableScroll = () => {
    const tableScroll = tableScrollRef.current;
    if (!tableScroll) return;

    scrollPositionRef.current = {
      x: tableScroll.scrollLeft,
      y: tableScroll.scrollTop,
    };
  };

  const detailsClickHandler = () => {
    if (contextMenuData) setSegregationModal(contextMenuData);
    handleCloseContextMenu();
  };

  const getHeadersSortingIcon = (theader: Header) => {
    if (!sortingType) return null;

    const sortingTypeParts = sortingType.split("_");
    const sortingTypeHeaderName = sortingTypeParts.slice(0, -1).join("_");
    if (theader.headerName === sortingTypeHeaderName) {
      if (sortingTypeParts[sortingTypeParts.length - 1] === "desc")
        return <SouthIcon className="!text-xs" />;
      if (sortingTypeParts[sortingTypeParts.length - 1] === "asc")
        return <NorthIcon className="!text-xs" />;
    }
  };

  const getTableHeaderJSX = (theader: Header, className?: string) => (
    <TableCell
      key={theader.headerName}
      rowSpan={dynamicHeaders.length ? 2 : undefined}
      className={`${sortableHeaderCellClassName} ${theader.classname ?? ""} ${getColumnTextClassName(theader.headerName)} ${className ?? ""}`}
      onClick={() => tableHeaderClickHandler(theader)}
    >
      {renderHeaderText(theader.translateHeaderName)}
      {getHeadersSortingIcon(theader)}
    </TableCell>
  );

  const renderHeaderRows = () => (
    <>
      <TableRow ref={firstHeaderRowRef}>
        {fixedFirstHeaders.map((header) => (
          <TableCell
            key={header.headerName}
            ref={(el) => {
              fixedFirstHeaderCellRefs.current[header.headerName] =
                el as HTMLTableCellElement | null;
            }}
            rowSpan={dynamicHeaders.length ? 2 : undefined}
            style={{ right: getFixedFirstRightOffset(header) }}
            className={`${sortableHeaderCellClassName} !sticky !z-[100] !bg-sky-50 fixed-column ${getFixedFirstInnerCellClassName(header)} ${getColumnTextClassName(header.headerName)} ${header.classname ?? ""}`}
            onClick={() => tableHeaderClickHandler(header)}
          >
            {renderHeaderText(header.translateHeaderName)}
            {getHeadersSortingIcon(header)}
          </TableCell>
        ))}
        {scrollableStaticHeaders.map((header) => getTableHeaderJSX(header))}
        {dynamicGroups.map((group, index) => (
          <TableCell
            key={group.groupName}
            colSpan={group.headers.length}
            sx={getGroupHeaderSx(group.groupName)}
            className={`${headerCellClassName} ${groupHeaderClassName?.(group.groupName) ?? ""} ${index === 0 ? "!border-r-2 !border-r-sky-200 !border-l-2 !border-b-2 !border-b-sky-200 !border-l-sky-200" : ""} ${index > 0 ? "!border-l-2 !border-b-2 !border-b-sky-200 !border-l-sky-200" : ""}`}
          >
            {renderHeaderText(group.groupName)}
          </TableCell>
        ))}
      </TableRow>
      {dynamicHeaders.length ? (
        <TableRow>
          {dynamicHeaders.map((header) => (
            <TableCell
              key={header.headerName}
              className={`${sortableHeaderCellClassName} ${header.classname ?? ""} ${getColumnTextClassName(header.headerName)} ${getGroupCellClassName(header, dynamicGroups)}`}
              onClick={() => tableHeaderClickHandler(header)}
            >
              {renderHeaderText(header.translateHeaderName)}
              {getHeadersSortingIcon(header)}
            </TableCell>
          ))}
        </TableRow>
      ) : null}
    </>
  );

  const renderRow = (
    row: TRow,
    rowKey: string | number,
    rowClassName: string,
    onClick?: () => void,
    isExpanded?: boolean,
  ) => {
    const clonedRow = JSON.parse(JSON.stringify(row));
    const showExpandIcon = row.isFooter === true && Boolean(row.groupName);

    const renderCellContent = (header: Header) => (
      <div
        className={`flex flex-row justify-center items-center gap-2 !w-full ${getColumnTextClassName(header.headerName)}`}
      >
        {showExpandIcon && header.headerName === expandIconHeaderName ? (
          <ExpandMoreIcon
            fontSize="small"
            className={`!transition-transform ${isExpanded ? "!rotate-180" : ""}`}
          />
        ) : showExpandIcon && header.headerName === groupTitleHeaderName ? (
          <span className="font-bold">{row.groupName}</span>
        ) : (
          <Cell
            onAction={onAction}
            clonedRow={clonedRow}
            headerName={header.headerName}
            type={header.typeStr}
          />
        )}
      </div>
    );

    return (
      <TableRow key={rowKey} className={rowClassName} onClick={onClick}>
        {fixedFirstHeaders.map((header) => (
          <TableCell
            key={`${header.headerName}-${rowKey}`}
            style={{ right: getFixedFirstRightOffset(header) }}
            className={`!sticky !z-50 !bg-sky-50 fixed-column ${getFixedFirstInnerCellClassName(header)} ${getColumnTextClassName(header.headerName)}`}
          >
            {renderCellContent(header)}
          </TableCell>
        ))}
        {scrollableStaticHeaders.map((header) => (
          <TableCell
            key={`${header.headerName}-${rowKey}`}
            className={getColumnTextClassName(header.headerName)}
          >
            {renderCellContent(header)}
          </TableCell>
        ))}
        {dynamicHeaders.map((header) => (
          <TableCell
            key={`${header.headerName}-${rowKey}`}
            className={`${getGroupCellClassName(header, dynamicGroups)} ${getColumnTextClassName(header.headerName)}`}
          >
            <div
              className={`flex flex-row justify-center items-center gap-2 !w-full ${getColumnTextClassName(header.headerName)}`}
              onContextMenu={(e) =>
                dynamicCellRightClickHandler(e, row, header)
              }
            >
              <Cell
                onAction={onAction}
                clonedRow={clonedRow}
                headerName={header.headerName}
                type={header.typeStr}
              />
            </div>
          </TableCell>
        ))}
      </TableRow>
    );
  };

  const renderBody = () => {
    if (!headers.length || !visibleRows.length) {
      return (
        <TableRow className="h-10 !w-full">
          {[...fixedFirstHeaders, ...scrollableStaticHeaders, ...dynamicHeaders].map(
            (header) => (
              <TableCell
                key={`empty-${header.headerName}`}
                className={`!text-center ${getColumnTextClassName(header.headerName)}`}
              >
                -
              </TableCell>
            ),
          )}
        </TableRow>
      );
    }

    return visibleRows.map((row) => {
      const isGroupFooter = Boolean(row.groupName && row.isFooter);
      const isExpanded =
        row.isFooter === true && row.groupName
          ? !collapsedGroups.has(row.groupName)
          : false;

      return renderRow(
        row,
        getStableRowKey(row),
        isGroupFooter
          ? "portfolio-overview-row bg-slate-200 !transition-all !w-full !relative !cursor-pointer"
          : row.isFooter
            ? "portfolio-overview-row bg-slate-200 !transition-all !w-full !relative"
            : "portfolio-overview-row !transition-all !w-full !relative",
        isGroupFooter && row.groupName
          ? () => toggleGroup(row.groupName!)
          : undefined,
        isExpanded,
      );
    });
  };

  const multiRowStickyHeaderSx = dynamicHeaders.length
    ? {
        "& .MuiTableCell-head": {
          backgroundColor: "#f0f9ff",
        },
        "& thead tr:first-of-type .MuiTableCell-head": {
          top: 0,
          zIndex: 3,
        },
        "& thead tr:nth-of-type(2) .MuiTableCell-head": {
          top: firstRowHeight,
          zIndex: 2,
        },
      }
    : undefined;

  return (
    <>
      <Menu
        anchorEl={contextMenuAnchor}
        open={Boolean(contextMenuAnchor)}
        onClose={handleCloseContextMenu}
      >
        <MenuItem onClick={detailsClickHandler}>جزئیات</MenuItem>
      </Menu>
      {segregationModal ? (
        <PortfoSegregationModal
          accountId={segregationModal.accountId}
          tag={segregationModal.tag}
          onClose={() => setSegregationModal(null)}
        />
      ) : null}
      <div
        className={`portfolio-overview-table w-full m-auto border-2 border-sky-100 rounded ${
          tableBoxClassName?.includes("overflow-") ? "" : "overflow-auto"
        } ${tableBoxClassName ?? ""}`}
        ref={tableScrollRef}
        onScroll={handleTableScroll}
      >
        <MuiTable
          stickyHeader
          className="relative min-h-[100px]"
          sx={multiRowStickyHeaderSx}
        >
          {columnWidthPercentages ? (
            <colgroup>
              {[
                ...fixedFirstHeaders,
                ...scrollableStaticHeaders,
                ...dynamicHeaders,
              ].map((header) => (
                <col
                  key={header.headerName}
                  style={{
                    width: `${columnWidthPercentages[header.headerName] ?? 0}%`,
                  }}
                />
              ))}
            </colgroup>
          ) : null}
          <TableHead>{renderHeaderRows()}</TableHead>
          <TableBody>{renderBody()}</TableBody>
        </MuiTable>
      </div>
    </>
  );
};

export default GroupTableUI;
